/**
 * Piezas de la Hoja de Rumbo para las ventanas heredadas (H01). `Modal` las
 * usa solo para los modales registrados en `modalesEnHoja.ts` y en el móvil;
 * cada modal migrado compone su cuerpo con ellas. Guía: docs-vadym/hojas.md.
 */
import type { ComponentProps, CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { Boton } from '#components/mobile/ui/Boton';
import { Icono } from '#components/mobile/ui/Icono';
import type { NombreIcono } from '#components/mobile/ui/Icono';
import { IconoCaja } from '#components/mobile/ui/IconoCaja';
import { Importe } from '#components/mobile/ui/Importe';
import { color, num, texto } from '#components/mobile/ui/tokens';

/** Tono de la cajita del icono: rojo si la hoja borra o cierra algo. */
export type TonoHoja = 'normal' | 'destructivo' | 'aviso';

const TONO_CAJA = {
  normal: 'acento',
  destructivo: 'rojo',
  aviso: 'aviso',
} as const;

/** Asa de la hoja: se baja para cerrarla. */
export { AsaHoja } from '#components/mobile/ui/AsaHoja';

type CabeceraHojaProps = {
  title?: ReactNode;
  /** Línea pequeña bajo el título (p. ej. la categoría de unas notas). */
  subtitulo?: ReactNode;
  icono?: NombreIcono;
  tono?: TonoHoja;
  /** Normalmente el `ModalCloseButton`, que en la hoja ya es la X redonda. */
  rightContent?: ReactNode;
  leftContent?: ReactNode;
};

/** Cabecera: [icono] Título a la izquierda (solo primera mayúscula) · X. */
export function CabeceraHoja({
  title,
  subtitulo,
  icono,
  tono = 'normal',
  rightContent,
  leftContent,
}: CabeceraHojaProps) {
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        marginBottom: 12,
        flexShrink: 0,
      }}
    >
      {leftContent}
      {icono && <IconoCaja icono={icono} tono={TONO_CAJA[tono]} size={44} />}
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          role="heading"
          aria-level={2}
          style={{
            fontSize: 20,
            fontWeight: 800,
            letterSpacing: '-0.02em',
            lineHeight: 1.15,
            color: color.fg,
            overflowWrap: 'anywhere',
          }}
        >
          {title}
        </Text>
        {subtitulo && (
          <Text
            style={{
              ...texto.secundario,
              fontWeight: 600,
              color: color.fg3,
              marginTop: 2,
            }}
          >
            {subtitulo}
          </Text>
        )}
      </View>
      {rightContent}
    </View>
  );
}

type BotonCerrarHojaProps = {
  onPress: ComponentProps<typeof Button>['onPress'];
};

/** X redonda de 40 px, fácil con el pulgar. */
export function BotonCerrarHoja({ onPress }: BotonCerrarHojaProps) {
  const { t } = useTranslation();
  return (
    <Button
      variant="bare"
      aria-label={t('Close')}
      data-testid="hoja-cerrar"
      onPress={onPress}
      style={{
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: color.surface2,
        color: color.fg,
        flexShrink: 0,
      }}
    >
      <Icono nombre="x" size={16} />
    </Button>
  );
}

/** Párrafo de la hoja (15/500, gris; `<strong>` en tinta principal). */
export function TextoHoja({
  children,
  style,
}: {
  children: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <Text
      style={{
        display: 'block',
        fontSize: 15,
        fontWeight: 500,
        lineHeight: 1.4,
        color: color.fg2,
        margin: '0 0 14px',
        ...style,
      }}
    >
      {children}
    </Text>
  );
}

/** Etiqueta en mayúsculas pequeñas sobre un campo o una cifra. */
export function EtiquetaHoja({ children }: { children: ReactNode }) {
  return (
    <Text
      style={{
        ...texto.etiqueta,
        color: color.fg3,
        margin: '0 0 6px 2px',
        display: 'block',
      }}
    >
      {children}
    </Text>
  );
}

/** Caja gris con una etiqueta y una cifra grande en euros, tabular. */
export function CifraHoja({
  etiqueta,
  valor,
  testId,
}: {
  etiqueta: ReactNode;
  /** Céntimos enteros. */
  valor: number;
  testId?: string;
}) {
  return (
    <View
      style={{
        backgroundColor: color.surface2,
        borderRadius: 16,
        padding: '12px 14px',
        marginBottom: 14,
      }}
    >
      <EtiquetaHoja>{etiqueta}</EtiquetaHoja>
      <Importe
        valor={valor}
        tono="auto"
        data-testid={testId}
        style={{ ...texto.cifra, ...num, color: color.fg }}
      />
    </View>
  );
}

type CampoSelectorHojaProps = {
  etiqueta?: ReactNode;
  valor?: string;
  placeholder: string;
  onPress: () => void;
  error?: boolean;
  testId?: string;
};

/**
 * Campo que abre un selector a pantalla completa en vez de un desplegable:
 * así nada se sale de la hoja.
 */
export function CampoSelectorHoja({
  etiqueta,
  valor,
  placeholder,
  onPress,
  error = false,
  testId,
}: CampoSelectorHojaProps) {
  return (
    <View style={{ marginBottom: 16 }}>
      {etiqueta && <EtiquetaHoja>{etiqueta}</EtiquetaHoja>}
      <Button
        variant="bare"
        data-testid={testId}
        aria-label={valor ? undefined : placeholder}
        onPress={onPress}
        style={{
          minHeight: 52,
          borderRadius: 14,
          backgroundColor: color.surface,
          border: `1.5px solid ${error ? color.bad : color.line2}`,
          padding: '0 14px',
          justifyContent: 'space-between',
          gap: 8,
          fontSize: 15,
          fontWeight: 600,
          color: valor ? color.fg : color.fg3,
          textAlign: 'left',
        }}
      >
        <Text
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: valor ? 600 : 500,
          }}
        >
          {valor || placeholder}
        </Text>
        <Icono nombre="down" size={18} />
      </Button>
    </View>
  );
}

type AccionHoja = {
  texto: ReactNode;
  onPress?: () => void;
  /** Botón rojo: la acción borra o cierra algo. */
  destructiva?: boolean;
  /** `submit` dentro de un `<Form>`. */
  tipo?: 'button' | 'submit';
  isDisabled?: boolean;
  testId?: string;
};

type BotonesHojaProps = {
  accion: AccionHoja;
  onCancelar: () => void;
  textoCancelar?: ReactNode;
};

/** Botones de bloque a todo el ancho: la acción arriba, Cancelar debajo. */
export function BotonesHoja({
  accion,
  onCancelar,
  textoCancelar,
}: BotonesHojaProps) {
  const { t } = useTranslation();
  return (
    <View style={{ gap: 8, marginTop: 6, flexShrink: 0 }}>
      <Boton
        bloque
        variante="primario"
        type={accion.tipo ?? 'button'}
        isDisabled={accion.isDisabled}
        data-testid={accion.testId ?? 'hoja-accion'}
        onPress={accion.onPress}
        style={
          accion.destructiva
            ? { backgroundColor: color.bad, color: color.surface }
            : undefined
        }
      >
        {accion.texto}
      </Boton>
      <Boton
        bloque
        variante="fantasma"
        data-testid="hoja-cancelar"
        onPress={onCancelar}
      >
        {textoCancelar ?? t('Cancel')}
      </Boton>
    </View>
  );
}
